import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { UserContext } from "../../UserContext"; // ✅ Import User Context

const Cardio = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = "cardio"; // Category for fetching videos
  const [videos, setVideos] = useState([]); // State to hold videos
  const [loading, setLoading] = useState(true); // State to handle loading

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful
  const handleHelpfulClick = async (videoId) => {
    if (!user || !user.id) {
      alert("You must be logged in to mark as helpful.");
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error("Error marking video as helpful:", error);
    }
  };

  return (
    <div className="cardio-page page-container">
            <p class="disclaimer">
          Disclaimer: As an Amazon Associate, I earn from qualifying purchases. If you make a purchase through the links here, 
          I may earn a small commission at no extra cost to you. Thank you for your support.
          </p>
          <div className="micro-void"></div>
      <h1 className="page-title">Cardio (Endurance Training)</h1>

      {/* Include the image */}
      <img src="/images/wf-cardio.webp" alt="Person doing cardio exercise" className="page-image" width="330" />

      <p className="page-description">
        Cardio, or cardiovascular endurance training, is a key pillar of physical fitness that involves any activity designed to elevate your heart rate and improve oxygen circulation throughout your body.
      </p>

      <h2 className="section-title">Health Benefits of Cardio</h2>
      <p className="section-description">
        Incorporating cardio into your routine can lead to:
      </p>
      <ul className="content-list">
        <li>Improved heart and lung function</li>
        <li>Increased energy levels and endurance</li>
        <li>Enhanced calorie burning for weight management</li>
        <li>Reduced risk of chronic diseases like diabetes and hypertension</li>
        <li>Boosted mood and reduced symptoms of anxiety and depression</li>
      </ul>

      <hr className="section-divider" />

      {/* Habit Plan Section */}
      <div className="additional-content">
        <h2 className="section-title">Create a Cardio Habit Plan</h2>
        <p className="section-description">
          Incorporating regular cardio into your routine can provide lasting benefits. Here’s a simple framework to help you build a consistent habit:
        </p>
        <h3 className="content-subtitle">Framework for Building Cardio Habits:</h3>
        <ul className="content-list">
          <li>
            <strong>Start Small:</strong> Begin with manageable steps, like a 10-minute walk or jog each day.
          </li>
          <li>
            <strong>Track Your Progress:</strong> Use an app, journal, or fitness tracker to track your cardio sessions and measure improvement.
          </li>
          <li>
            <strong>Set Goals:</strong> Set achievable cardio goals, whether it's increasing distance, time, or intensity over time.
          </li>
        </ul>

        <h3 className="content-subtitle">Examples of Cardio Habits:</h3>
        <ul className="content-list">
          <li>Walk or jog for 20 minutes each morning.</li>
          <li>Cycle to work 3 times a week.</li>
          <li>Take the stairs instead of the elevator for better cardiovascular health.</li>
        </ul>
      </div>

      <hr className="section-divider" />

      {/* Curated Video Collection */}
      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        videos.length > 0 ? (
          videos.map((video) => (
            <div key={video._id} className="video-card">
              <h3 className="video-title">{video.title}</h3>
              <p className="video-details">
                <strong>Source:</strong> {video.source} <br />
                <strong>Length:</strong> {video.length} <br />
                {video.description}
              </p>
              <iframe
                className="video-frame"
                src={video.url}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>

              {/* ✅ Helpful Button */}
              <div className="video-actions">
                <button
                  onClick={() => handleHelpfulClick(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  className={`button ${video.usersWhoClickedHelpful?.includes(user?.id) ? "disabled" : ""}`}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-videos-message">No videos found for this category.</p>
        )
      )}
      
      <hr className="section-divider" />
<h2 className="section-title">Curated Book Collection</h2>

<div className="book-card">
  <h3 className="book-title">Runner's World Run Less, Run Faster</h3>

  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/8944262-M.jpg" // Open Library cover image URL
    alt="Runner's World Run Less, Run Faster by Bill Pierce, Scott Murr, Ray Moss" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>

  <div className="micro-void"></div>

  <p className="book-details">
    <strong>Author:</strong> Bill Pierce, Scott Murr, Ray Moss <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> Runner's World Run Less, Run Faster introduces a revolutionary 3-run-a-week training program designed to help runners train smarter, not harder. The book presents a training approach that optimizes quality over quantity, helping runners become faster and stronger while avoiding burnout. Suitable for anyone from those with hopes of qualifying for the Boston Marathon to novice runners just getting into the sport. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This book offers a time-efficient and scientifically-backed approach to running that promises faster race times without overtraining. It's a must-read for anyone looking to improve their performance while minimizing the risk of injury and burnout.
  </p>

  <div className="micro-void"></div>

  <a
    href="https://amzn.to/4iCuBzt" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4iCuBzt
  </a>

  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>


{/* Book 2: Born to Run */}
<div className="book-card">
  <h3 className="book-title">Born to Run</h3>

  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/8473285-M.jpg" // Open Library cover image URL
    alt="Born to Run by Christopher McDougall" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>

  <div className="micro-void"></div>

  <p className="book-details">
    <strong>Author:</strong> Christopher McDougall <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> Born to Run chronicles the author's exploration of the Tarahumara people of Mexico, renowned for their extraordinary long-distance running abilities. McDougall blends history, adventure, and science to reveal the secrets behind human endurance and the power of barefoot running. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This book is an inspiring and exciting exploration of human potential. Whether you’re a runner or just someone interested in pushing the limits of human endurance, it offers incredible insights into running as a natural, primal activity.
  </p>

  <div className="micro-void"></div>

  <a
    href="https://amzn.to/4bHDdCD" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4bHDdCD
  </a>

  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

{/* Book 3: Run for Your Life */}
<div className="book-card">
<h3 className="book-title">I Hate Running and You Can Too</h3>

{/* Book Cover Image from Open Library */}
<img 
  src="https://covers.openlibrary.org/b/id/10864663-M.jpg" // Open Library cover image URL
  alt="I Hate Running and You Can Too by Brendan Leonard" 
  className="book-cover" 
/>

{/* Attribution for Image Source */}
<p className="image-source">Image Source: Open Library API</p>

<div className="micro-void"></div>

<p className="book-details">
  <strong>Author:</strong> Brendan Leonard <br />
  <div className="micro-void"></div>
  <strong>Overview:</strong> I Hate Running and You Can Too is a humorous yet insightful look at running for fitness, especially for those who are not natural-born runners. Mark Remy offers advice, motivation, and practical tips to help readers push through their resistance to running and improve their cardiovascular health. <br />
  <div className="barely-void"></div>
  <strong>Why It's Great:</strong> This book makes running approachable for those who don't enjoy it. It's a light-hearted yet practical guide that combines humor with actionable tips to help beginners embrace the challenge of running.
</p>

<div className="micro-void"></div>

<a
  href="https://amzn.to/4ivnmJA" // Your actual Amazon affiliate link
  className="amazon-link"
  target="_blank"
  rel="noopener noreferrer"
>
  Buy on Amazon-https://amzn.to/4ivnmJA
</a>

{/* Affiliate Link Disclosure */}
<p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
<div className="short-line"></div>
</div>



      <hr className="section-divider" />

      {/* Popular Cardio Apps */}
      <div className="additional-content">
        <h2 className="section-title">Popular Cardio Apps</h2>
        <p className="section-description">
          There are many apps that can help you with your cardio training, whether you're a beginner or training for a marathon.
        </p>
        <h3 className="content-subtitle">Strava</h3>
        <p>Strava is a popular app for tracking running, cycling, and other endurance activities.</p>
        <h3 className="content-subtitle">Nike Training Club</h3>
        <p>Nike offers guided cardio workouts for every fitness level.</p>
        <h3 className="content-subtitle">Map My Run</h3>
        <p>Map My Run is a great app for tracking running and walking, with detailed route and distance tracking features.</p>
      </div>

      <hr />

      {/* Forum Section */}
      <div className="forum-section">
        <h2 className="section-title">Join the Cardio Discussion</h2>
        <p className="section-description">
          Have questions or insights about cardio? Join our forum to connect with others who are exploring this topic.
        </p>
        <a href="/forum/Cardio" className="forum-link">
          Go to Cardio Forum
        </a>
      </div>
    </div>
  );
};

export default Cardio;
